import React from 'react';
import { RefreshCw, FileDown } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SourceMode = 'off-market' | 'on-market';
export type FinancingMode = 'conventional' | 'seller' | 'cash';
export type TaxViewMode = 'std-clean' | 'obbb-clean' | 'std-1031' | 'obbb-1031';

export interface ScenarioControlBarProps {
  source: SourceMode;
  financing: FinancingMode;
  taxView: TaxViewMode;
  onSourceChange: (source: SourceMode) => void;
  onFinancingChange: (financing: FinancingMode) => void;
  onTaxViewChange: (taxView: TaxViewMode) => void;
  onReAnalyze?: () => void;
  onExportPdf?: () => void;
  isAnalyzing?: boolean;
}

// ---------------------------------------------------------------------------
// Toggle group (reusable)
// ---------------------------------------------------------------------------

interface ToggleGroupProps<T extends string> {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}

function ToggleGroup<T extends string>({ label, value, options, onChange }: ToggleGroupProps<T>) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-semibold tracking-widest uppercase text-stone-400">
        {label}
      </span>
      <div className="flex bg-stone-800/60 rounded-lg p-0.5">
        {options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 whitespace-nowrap ${
              value === opt.value
                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30 shadow-[0_0_8px_rgba(212,175,55,0.15)]'
                : 'text-stone-400 hover:text-stone-200'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ScenarioControlBar: React.FC<ScenarioControlBarProps> = ({
  source,
  financing,
  taxView,
  onSourceChange,
  onFinancingChange,
  onTaxViewChange,
  onReAnalyze,
  onExportPdf,
  isAnalyzing = false,
}) => {
  return (
    <div className="sticky top-16 z-30 bg-stone-900/95 backdrop-blur border-b border-stone-800 px-4 py-2.5 flex flex-wrap items-center gap-4">
      {/* Source toggle */}
      <ToggleGroup
        label="Source"
        value={source}
        options={[
          { value: 'off-market', label: 'Off-Market' },
          { value: 'on-market', label: 'On-Market' },
        ]}
        onChange={onSourceChange}
      />

      <div className="w-px h-6 bg-stone-700" />

      {/* Financing toggle */}
      <ToggleGroup
        label="Financing"
        value={financing}
        options={[
          { value: 'conventional', label: 'Conventional' },
          { value: 'seller', label: 'Seller Fin.' },
          { value: 'cash', label: 'Cash' },
        ]}
        onChange={onFinancingChange}
      />

      <div className="w-px h-6 bg-stone-700" />

      {/* Tax View toggle — 4 scenarios matching solver */}
      <ToggleGroup
        label="Tax View"
        value={taxView}
        options={[
          { value: 'std-clean', label: 'Standard' },
          { value: 'obbb-clean', label: 'OBBB' },
          { value: 'std-1031', label: '1031' },
          { value: 'obbb-1031', label: 'OBBB + 1031' },
        ]}
        onChange={onTaxViewChange}
      />

      {/* Spacer */}
      <div className="flex-1" />

      {/* Action buttons */}
      {onReAnalyze && (
        <button
          onClick={onReAnalyze}
          disabled={isAnalyzing}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold bg-gold/90 text-slate-900 rounded-lg hover:bg-gold transition-colors disabled:opacity-50"
        >
          <RefreshCw size={13} className={isAnalyzing ? 'animate-spin' : ''} />
          Re-Analyze
        </button>
      )}
      {onExportPdf && (
        <button
          onClick={onExportPdf}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-stone-600 text-stone-300 rounded-lg hover:border-stone-400 hover:text-ivory transition-colors"
        >
          <FileDown size={13} />
          Export PDF
        </button>
      )}
    </div>
  );
};
